import React, { useRef, useState } from 'react';
import { NavButtons } from './QuoteFormField';

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;

export default function QuoteStepDocuments({ files, onChange, onNext, onBack }) {
  const inputRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState('');

  const addFiles = (newFiles) => {
    setFileError('');
    const toAdd = [];
    for (const f of newFiles) {
      if (!ACCEPTED_TYPES.includes(f.type)) {
        setFileError('This file type is not supported. Please upload a PDF, JPG, or PNG under 10MB.');
        continue;
      }
      if (f.size > MAX_SIZE) {
        setFileError('This file is too large. Please upload files under 10MB.');
        continue;
      }
      if (files.length + toAdd.length >= MAX_FILES) break;
      toAdd.push({ name: f.name, size: f.size, type: f.type, file: f });
    }
    onChange([...files, ...toAdd]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (i) => onChange(files.filter((_, idx) => idx !== i));

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Upload Documents (Optional)</h2>
        <p className="text-gray-500">
          Speed up your quote by uploading a copy of your current declarations page, driver's licenses, or prior loss runs. This step is optional.
        </p>
      </div>

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-[#1e3a5f] bg-blue-50' : 'border-gray-300 hover:border-[#1e3a5f]'
        }`}
      >
        <div className="text-4xl mb-3">📎</div>
        <p className="text-gray-700 font-medium">Drag & drop files here, or click to browse</p>
        <p className="text-sm text-gray-500 mt-1">PDF, JPG, PNG — up to 10MB each — max {MAX_FILES} files</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png"
          className="hidden"
          onChange={e => addFiles(Array.from(e.target.files))}
        />
      </div>

      {fileError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {fileError}
        </div>
      )}

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="text-lg">📄</span>
                <div>
                  <div className="text-sm font-medium text-gray-900">{f.name}</div>
                  <div className="text-xs text-gray-500">{formatSize(f.size)}</div>
                </div>
              </div>
              <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500 transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="pt-2 space-y-3">
        <button
          onClick={onNext}
          className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white font-semibold py-3 rounded-xl transition-colors"
        >
          {files.length > 0 ? 'Continue to Review' : 'Skip & Continue'}
        </button>
        <button
          onClick={onBack}
          className="w-full text-gray-500 hover:text-gray-700 text-sm font-medium py-2 transition-colors"
        >
          ← Back
        </button>
      </div>
    </div>
  );
}
